import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane, FaEnvelope, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import BlurText from './ui/BlurText';

const ContactSection = () => {
  const [formData, setFormData] = useState({ email: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post('/sendmail', formData);
      setStatus({ type: 'success', message: '✨ Message sent successfully! We\'ll get back to you soon.' });
      setFormData({ email: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: '❌ Failed to send message. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 grid-pattern opacity-20"></div>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-10 md:mb-12"
        >
          <motion.div
            className="inline-flex items-center space-x-2 glass-strong px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <FaEnvelope className="text-primary text-sm sm:text-base" />
            <span className="text-xs sm:text-sm font-mono">Get In Touch</span>
          </motion.div>

          <BlurText
            text="Let's Connect"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-center"
            childClassName="gradient-text"
            delay={150}
            animateBy="words"
            direction="top"
          />
          <p className="text-gray-300 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-4">
            Have a question, idea, or just want to collaborate? We'd love to hear from you!
            Drop us a message and we'll respond as soon as possible.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="glass-strong rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 border border-primary/20"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-300">
                Your Email Address
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-300">
                Your Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                rows="6"
                placeholder="Tell us about your project, idea, or question..."
                required
              />
              <div className="mt-2 text-xs text-gray-500 font-mono">
                {formData.message.length} characters
              </div>
            </div>

            {status.message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl flex items-center space-x-3 ${status.type === 'success'
                  ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                  : 'bg-red-500/20 border border-red-500/30 text-red-400'
                  }`}
              >
                {status.type === 'success' && <FaCheckCircle className="text-xl flex-shrink-0" />}
                <span className="text-sm">{status.message}</span>
              </motion.div>
            )}

            <motion.button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-primary to-secondary rounded-xl font-semibold flex items-center justify-center space-x-3 hover:shadow-2xl hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
              whileHover={{ scale: loading ? 1 : 1.02, y: loading ? 0 : -2 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin text-xl" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send Message</span>
                  <FaPaperPlane />
                </>
              )}
            </motion.button>
          </div>
        </motion.form>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="text-gray-400 text-sm">
            You can also reach us at{' '}
            <a href="mailto:sdc@mitsgwalior.in" className="text-primary hover:text-secondary transition-colors font-semibold">
              sdc@mitsgwalior.in
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
